import { ChangeDetectorRef } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Checkpoint } from '../../../../models';

export function normalizeCheckpointValueList(values: string[] | null | undefined): string[] {
  if (values == null) {
    return [];
  }
  return values.map(v => String(v)).filter(v => v !== '');
}

/** Updates form `checkpointValues` from lookup selection when the row includes `values` (list API). Skips stub items (e.g. byKey with only id+name). Clears when selection is empty. */
export function applyCheckpointValuesFromLookupItem(
  checkpointValuesCtrl: AbstractControl,
  selectedItem: Checkpoint | Record<string, unknown> | null | undefined,
): void {
  if (selectedItem == null) {
    checkpointValuesCtrl.setValue([], { emitEvent: true });
    return;
  }
  if (!Object.prototype.hasOwnProperty.call(selectedItem, 'values')) {
    return;
  }
  const raw = (selectedItem as Checkpoint).values;
  checkpointValuesCtrl.setValue(normalizeCheckpointValueList(raw), { emitEvent: true });
}

export function stepHasCheckpoint(checkpointId: unknown): boolean {
  return checkpointId !== null && checkpointId !== undefined && checkpointId !== '';
}

/** A step without checkpoint cannot be required: forces `required` to false and disables the switch until a checkpoint is selected. */
export function syncRequiredControlWithCheckpoint(
  form: FormGroup,
  destroy$: Observable<unknown>,
  cdr: ChangeDetectorRef,
): void {
  const checkpointCtrl = form.get('checkpointId');
  const requiredCtrl = form.get('required');
  if (!checkpointCtrl || !requiredCtrl) {
    return;
  }
  const apply = (checkpointId: unknown): void => {
    if (requiredCtrl.root.disabled) {
      return;
    }
    if (!stepHasCheckpoint(checkpointId)) {
      requiredCtrl.setValue(false, { emitEvent: false });
      requiredCtrl.disable({ emitEvent: false });
    } else {
      requiredCtrl.enable({ emitEvent: false });
    }
    cdr.markForCheck();
  };
  apply(checkpointCtrl.value);
  checkpointCtrl.valueChanges
    .pipe(takeUntil(destroy$))
    .subscribe(v => apply(v));
}

/** Enforces allowed list: disables control when empty; filters selection when not. */
export function syncDefaultValuesControlWithChoices(
  defaultValuesCtrl: AbstractControl,
  choices: string[],
): void {
  if (defaultValuesCtrl.root.disabled) {
    return;
  }
  if (choices.length === 0) {
    defaultValuesCtrl.setValue([], { emitEvent: false });
    defaultValuesCtrl.disable({ emitEvent: false });
    return;
  }
  defaultValuesCtrl.enable({ emitEvent: false });
  const cur = defaultValuesCtrl.value;
  const arr = Array.isArray(cur) ? (cur as unknown[]).map(String) : [];
  defaultValuesCtrl.setValue(arr.filter(x => choices.includes(x)), { emitEvent: false });
}
